import { useCallback, useEffect, useRef, useState } from "react";
import {
    startSession, ask, getNotices, getPublicList, logMessage, createInquiry, getMySession,
} from "../customerApi";
import { menusFrom, NOTICE_KEY, matchCategory, inquiryTypeOf, menuForSubcategory, isReceiveAddress } from "./menus";
import { koText } from "./text";

const IMG = /^image\//;
const DONE = { paper: "cf.done.paper", sales: "cf.done.sales", menu: "cf.done.menu", printer: "cf.done.printer", pay: "cf.done.printer", other: "cf.done.printer" };
const ASK = { menu: "cf.ask.menu", printer: "cf.ask.printer", pay: "cf.ask.pay", other: "cf.ask.other" };
// 입력창 글이 답이 되는 단계 — 그 밖(가맹점 확인·기간 버튼)에서 글을 보내면 흐름을 끝내고 자유 질문
const TEXT_STEPS = ["text", "receive", "reask"];

// 가맹점주 챗봇 흐름(시안 onMenu · flowYes · sendText · #reask · #cont).
// 대화는 데이터(messages)로 들고, 버튼·비동기 처리는 ref 로 최신 흐름·세션을 읽는다(렌더 때 값이 굳지 않게).
export default function useChatFlow({ enabled, customer, lang, t }) {
    const [messages, setMessages] = useState([]);
    // 「처음으로」 누른 때의 마지막 말 번호 — 그 앞 말은 이전 내역으로 위에 남고, 첫 화면(환영·메뉴)은 그 뒤에 새로 그린다
    const [homeMark, setHomeMark] = useState(0);
    const [flow, setFlowState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [files, setFiles] = useState([]); // [{file, url}]
    const [sessionId, setSessionId] = useState(null);
    const [categories, setCategories] = useState([]);
    const [nonce, setNonce] = useState(0);
    const idRef = useRef(0);
    const flowRef = useRef(null);
    const sessionRef = useRef(null);
    const filesRef = useRef([]);
    const busyRef = useRef(false);
    const catsRef = useRef([]);
    const customerRef = useRef(customer);
    customerRef.current = customer;
    const L = lang === "zh" ? "zh" : "ko";

    const setFlow = (f) => { flowRef.current = f; setFlowState(f); };
    const setSession = (id) => { sessionRef.current = id; setSessionId(id); };
    const setBusyBoth = (v) => { busyRef.current = v; setBusy(v); };
    // 번호는 바로 매긴다 — 같은 이벤트에서 이어 부르는 「처음으로」(cancel)가 방금 넣은 말까지 이전 내역으로 본다
    const push = (...items) => {
        const made = items.map((x) => ({ id: ++idRef.current, at: new Date(), ...x }));
        setMessages((m) => [...m, ...made]);
    };
    const newFlowId = () => ++idRef.current;

    // 메뉴 흐름 말은 대화 기록에 남긴다(자유 질문·음성은 서버가 스스로 남긴다)
    const sayUser = (text, log = false) => { push({ role: "user", text }); if (log) logMessage(sessionRef.current, "user", text); };
    const sayBot = (text, card, log = false) => { push({ role: "bot", text, ...(card ? { card } : {}) }); if (log && text) logMessage(sessionRef.current, "assistant", text); };

    const openSession = useCallback(() => {
        startSession().then((id) => setSession(id)).catch(() => {});
    }, []);
    useEffect(() => {
        if (!enabled) return;
        if (!sessionRef.current) openSession();
        getPublicList("/public/quick-categories").then((l) => { catsRef.current = l || []; setCategories(l || []); }).catch(() => {});
    }, [enabled, nonce, openSession]);

    const clearFiles = () => {
        filesRef.current.forEach((f) => { if (f.url) URL.revokeObjectURL?.(f.url); });
        filesRef.current = [];
        setFiles([]);
    };
    // 대화·흐름만 비운다(언어 전환) — 세션은 유지
    const clear = useCallback(() => { setMessages([]); setHomeMark(0); setFlow(null); clearFiles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // 로그아웃·끊김 — 세션까지 버리고, 다시 켜지면 새로 연다
    const reset = useCallback(() => { clear(); setSession(null); setNonce((n) => n + 1); }, [clear]);
    // 「처음으로」 — 흐름만 끝내고 대화는 남긴다
    const cancel = () => { setFlow(null); clearFiles(); setHomeMark(idRef.current); };
    // 화면 밖 안내 한 줄(음성 오류 등). reopen 이면 새 세션
    const notice = (text, reopen = false) => { sayBot(text); if (reopen) openSession(); };

    // ----- 자유 질문 -----
    const showAnswer = (data) => {
        const md = (data?.answer || "").trim() || t("common.answerUnavailable");
        if (data?.reason_code === "MY_INQUIRIES") push({ role: "bot", md, card: { type: "inquiries", items: data.inquiries || [], goInq: true } });
        else push({ role: "bot", md });
    };
    const qa = async (text) => {
        setBusyBoth(true);
        try {
            showAnswer(await ask(sessionRef.current, text, L));
        } catch (e) {
            if (e?.newSession) {
                openSession();
                sayBot(e.status === 409 ? (e.message || t("chat.maxMessages")) : t("chat.sessionGone"));
            } else sayBot(t("common.serverError"));
        }
        setBusyBoth(false);
    };
    // 음성: 받아쓴 글을 가맹점주 말로, 답이 같이 왔으면 그대로(아니면 질의응답)
    const voice = async (data) => {
        const text = data?.question || data?.text || "";
        if (text) sayUser(text);
        if (data?.answer) showAnswer(data);
        else if (text) await qa(text);
    };

    // ----- 공지 -----
    const startNotice = async () => {
        const items = await getNotices().catch(() => []);
        sayBot(items.length ? t("cf.notice.prompt") : t("cf.empty.notice"), items.length ? { type: "notices", items } : null);
    };
    const openNotice = (n) => {
        sayUser((L === "zh" && n.title_zh) || n.title);
        push({ role: "bot", md: (L === "zh" && n.content_zh) || n.content, breaks: true, html: true });
    };

    // ----- 사진(접수 단계, 이미지 3장까지) -----
    const addFiles = (list) => {
        const picked = [...(list || [])];
        if (!picked.length) return;
        if (picked.some((f) => !IMG.test(f.type)) || filesRef.current.length + picked.length > 3) { sayBot(t("cf.fail.photo")); return; }
        const next = [...filesRef.current, ...picked.map((file) => ({ file, url: URL.createObjectURL?.(file) || "" }))];
        filesRef.current = next;
        setFiles(next);
    };
    const removeFile = (i) => {
        const gone = filesRef.current[i];
        if (gone?.url) URL.revokeObjectURL?.(gone.url);
        const next = filesRef.current.filter((_, j) => j !== i);
        filesRef.current = next;
        setFiles(next);
    };

    // ----- 접수 — 성공이면 완료 문구(병합이면 덧붙임 안내) + 카드, 흐름 끝. 실패면 단계 유지. -----
    const submit = async (f, { content, email, reopenedFromId }) => {
        if (busyRef.current) return false;
        setBusyBoth(true);
        const key = f.menu === "reask" ? menuForSubcategory(f.inquiry?.subcategory?.label) : f.menu;
        // 메뉴(카테고리)로 시작한 접수는 그 카테고리, 재문의는 이전 문의 세부에 맞는 카테고리
        const catId = f.catId ?? matchCategory(key, catsRef.current)?.id;
        const out = await createInquiry({
            content, email, reopenedFromId,
            quickCategoryId: catId,
            inquiryType: f.menu === "reask" ? (key ? inquiryTypeOf(key) : "other") : inquiryTypeOf(f.menu),
            chatSessionId: sessionRef.current,
            businessName: customerRef.current?.business_name,
            files: filesRef.current.map((x) => x.file),
        });
        setBusyBoth(false);
        if (out.ok) {
            const q = out.data || {};
            const text = q.merged ? t("cf.merged", { id: q.id })
                : f.menu === "reask" ? t("cf.done.reask", { id: f.inquiry.id }) : t(DONE[f.menu]);
            sayBot(text, { type: "inquiry", inquiry: q }, true);
            // 접수하는 사이 다른 메뉴를 골랐으면 그 새 흐름(과 사진)은 건드리지 않는다
            if (flowRef.current?.id === f.id) { setFlow(null); clearFiles(); }
            return true;
        }
        if (out.code === "invalid_email") sayBot(t("cf.bad.receive"), null, true);
        else if (out.code === "not_an_image" || (typeof out.code === "string" && out.code.startsWith("attachments"))) sayBot(t("cf.fail.photo"));
        else if (out.code === "inquiry_not_completed") { sayBot(t("cf.fail.notCompleted"), { type: "goInq" }); setFlow(null); }
        else if (out.status === 404 && f.menu === "reask") { sayBot(t("cf.fail.notFound")); setFlow(null); }
        else sayBot(t("cf.fail"));
        return false;
    };

    // ----- 메뉴 -----
    const startMenu = async (key) => {
        const m = menusFrom(catsRef.current, L).find((x) => x.key === key);
        if (!m) return;
        setFlow(null);
        clearFiles();
        if (m.key === NOTICE_KEY) { sayUser(t("notice.title")); await startNotice(); return; }
        sayUser(m.title, true);
        const f = { id: newFlowId(), menu: m.flow, catId: m.cat.id, catName: m.cat.name, step: "confirm" };
        setFlow(f);
        sayBot(null, { type: "store", flowId: f.id, customer: customerRef.current }); // 시안처럼 말풍선 없이 카드만
    };

    const confirmStore = async (flowId, yes) => {
        const f = flowRef.current;
        if (!f || f.id !== flowId || f.step !== "confirm") return;
        if (f.menu === "paper" && yes) {
            const nf = { ...f, step: "submitting" };
            setFlow(nf);
            sayUser(t("cf.yes"), true);
            const ok = await submit(nf, { content: f.catName || koText("home.paper.title") });
            if (!ok && flowRef.current === nf) setFlow(f); // 실패면 다시 「예」를 누를 수 있게
            return;
        }
        sayUser(t(yes ? "cf.yes" : "cf.no"), true);
        if (!yes) { cancel(); return; } // 정보가 다르면 접수하지 않고 「처음으로」처럼 새 첫 화면
        if (f.menu === "sales") {
            setFlow({ ...f, step: "period" });
            sayBot(t("cf.ask.period"), { type: "periods", flowId: f.id }, true);
            return;
        }
        setFlow({ ...f, step: "text" });
        sayBot(t(ASK[f.menu]), null, true);
    };

    const pickPeriod = (flowId, label) => {
        const f = flowRef.current;
        if (!f || f.id !== flowId || f.step !== "period") return;
        sayUser(label, true);
        setFlow({ ...f, step: "receive", period: label });
        sayBot(t("cf.ask.receive"), null, true);
    };

    // ----- 입력창 -----
    const send = async (text) => {
        const body = (text || "").trim();
        if (!body || busyRef.current) return;
        const f = flowRef.current;
        if (f?.step === "submitting") return;
        if (f && TEXT_STEPS.includes(f.step)) {
            sayUser(body, f.menu !== "reask");
            if (f.step === "receive" && !isReceiveAddress(body)) { sayBot(t("cf.bad.receive"), null, true); return; }
            const payload = f.step === "receive"
                ? { content: `[${f.period}] ${koText("inquiry.receiveMethodPrefix")}${body}`, email: body }
                : f.step === "reask" ? { content: body, reopenedFromId: f.inquiry.id } : { content: body };
            const sending = { ...f, step: "submitting" };
            setFlow(sending);
            const ok = await submit(sending, payload);
            if (!ok && flowRef.current === sending) setFlow(f); // 실패면 같은 단계로 — 다시 보낼 수 있게
            return;
        }
        if (f) setFlow(null);
        sayUser(body);
        await qa(body);
    };

    // ----- 재문의(완료 문의 상세에서) — 다음 글로 reopened_from_id 접수 -----
    const startReask = (q) => {
        setFlow(null);
        clearFiles();
        const sub = q?.subcategory?.label || q?.category?.label || "";
        sayUser(t("cf.reask.user", { sub, id: q.id }));
        sayBot(q?.process_content ? t("cf.reask.bot", { result: q.process_content }) : t("cf.reask.botNoResult"));
        setFlow({ id: newFlowId(), menu: "reask", step: "reask", inquiry: q });
    };

    // ----- 이어서 질문(대화 보기에서) — 지난 말을 불러오고 그 세션으로 이어 묻는다 -----
    const resume = async (id) => {
        setFlow(null);
        clearFiles();
        try {
            const s = await getMySession(id);
            const past = (s?.messages || []).map((m) => ({
                ...(m.role === "user" ? { role: "user", text: m.content } : { role: "bot", md: m.content }),
                at: m.created_at ? new Date(m.created_at) : null,
            }));
            setMessages(past.map((x) => ({ id: ++idRef.current, ...x })));
            setSession(id);
            sayBot(t("cf.resumed"));
        } catch {
            sayBot(t("cf.resumeGone"));
        }
    };

    return {
        messages, homeMark, flow, busy, files, sessionId, categories,
        startMenu, confirmStore, pickPeriod, send, voice,
        openNotice, startReask, resume, addFiles, removeFile, clear, reset, notice, cancel,
    };
}
